import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, FolderOpen, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { UploadDialog } from "@/components/UploadDialog";
import { MaterialRow } from "@/components/MaterialRow";
import { subjectById, KINDS } from "@/lib/constants";
import { api } from "@/lib/api";

export default function SubjectPage() {
  const { subject } = useParams();
  const { user } = useAuth();
  const s = subjectById(subject);
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("ALL");

  const load = useCallback(() => {
    api.get("/materials", { params: { grade: user.grade, subject } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [user.grade, subject]);
  useEffect(() => { load(); }, [load]);

  if (!s) return <Navigate to="/" replace />;
  const Icon = s.icon;
  const shown = (items || []).filter((m) => kind === "ALL" || m.kind === kind);

  return (
    <div>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2e7d56] hover:text-[#1f5f42]" data-testid="back-to-subjects-link">
        <ArrowLeft className="h-4 w-4" />Все предметы
      </Link>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-end justify-between gap-6 rise">
        <div className="flex items-center gap-5">
          <div className="h-16 w-16 rounded-2xl bg-[#1f5f42] text-[#a3e6be] grid place-items-center"><Icon className="h-8 w-8" /></div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2e7d56]">{user.grade} класс</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0b2e20] mt-1" data-testid="subject-title">{s.name}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/chat?channel=subj-${s.id}`} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border-2 border-[#1f5f42] text-[#1f5f42] font-semibold hover:bg-[#e8f5ed]" data-testid="subject-chat-link"><MessageCircle className="h-4 w-4" />Обсудить</Link>
          <UploadDialog defaultSubject={s.id} />
        </div>
      </div>
      <div className="mt-10 flex gap-2 flex-wrap">
        {[{ id: "ALL", name: "Все" }, ...KINDS].map((k) => (
          <button key={k.id} onClick={() => setKind(k.id)} data-testid={`kind-filter-${k.id}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${kind === k.id ? "bg-[#0b2e20] text-white border-[#0b2e20]" : "bg-white text-[#1f5f42] border-[#d1e7dd] hover:bg-[#e8f5ed]"}`}>{k.name}</button>
        ))}
      </div>
      <div className="mt-6 space-y-3" data-testid="materials-list">
        {items === null && <p className="text-sm text-[#6b8a7b]">Загрузка...</p>}
        {items && shown.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-[#d1e7dd] rounded-2xl" data-testid="materials-empty">
            <FolderOpen className="h-10 w-10 mx-auto text-[#a3c9b4]" />
            <p className="font-semibold text-[#1f5f42] mt-3">Пока нет материалов для {user.grade} класса</p>
            <p className="text-sm text-[#6b8a7b] mt-1">Будь первым — загрузи файл и получи 50 коинов</p>
          </div>
        )}
        {shown.map((m) => <MaterialRow key={m.id} m={m} />)}
      </div>
    </div>
  );
}
